/**
 * Inline editor for an existing gratitude card.
 *
 * Mirrors AddGratitudeDialog's feature set so the edit experience matches
 * the create one: text, images, multi-select life areas, and free-form tags.
 * Visibility / paper color / recipient(s) live elsewhere and aren't changed
 * here — those flows each have their own trigger.
 */
import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { ImagePlus, X, Check } from "lucide-react";
import { toast } from "sonner";
import { compressImageToDataUrl } from "../utils/imageCompress";

const API = "/api";
const MAX_IMAGES = 4;
const MAX_CONTENT = 1800;
const MAX_AREAS = 5;
const MAX_TAGS = 10;

const EditGratitudeDialog = ({ open, card, onClose, onSaved }) => {
  const [content, setContent] = useState("");
  const [images, setImages] = useState([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [wheelAreaKeys, setWheelAreaKeys] = useState([]);
  const [wheelAreas, setWheelAreas] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagDraft, setTagDraft] = useState("");
  const fileInputRef = useRef(null);

  // Load the wheel-of-life areas once per open so the picker has labels/colors.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await fetch(`${API}/tools/wheel`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          // The endpoint returns { wheel: { areas: [...] }, default_areas: [...] }.
          // Prefer the user's saved customisation, fall back to platform defaults.
          const active = (data.wheel?.areas && data.wheel.areas.length > 0)
            ? data.wheel.areas
            : (data.default_areas || []);
          setWheelAreas(active);
        }
      } catch {
        /* silent — editor still works without the chip picker */
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open || !card) return;
    setContent(card.content || "");
    // Prefer the new `images` list; fall back to the legacy single field.
    setImages(
      Array.isArray(card.images) && card.images.length
        ? card.images
        : (card.image_data_url ? [card.image_data_url] : [])
    );
    // Prefer the new multi-area list; fall back to the legacy singular key.
    const initialKeys = Array.isArray(card.wheel_area_keys) && card.wheel_area_keys.length
      ? card.wheel_area_keys
      : (card.wheel_area_key ? [card.wheel_area_key] : []);
    setWheelAreaKeys(initialKeys);
    setTags(Array.isArray(card.tags) ? [...card.tags] : []);
    setTagDraft("");
    setImageUploading(false);
    setSaving(false);
  }, [open, card]);

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (images.length >= MAX_IMAGES) {
      toast.error(`Up to ${MAX_IMAGES} images per card`);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image is too large. Please pick one under 10 MB.");
      return;
    }
    setImageUploading(true);
    try {
      const dataUrl = await compressImageToDataUrl(file, {
        maxDim: 1200,
        maxBytes: 900_000,
        mimeType: file.type === "image/png" ? "image/png" : "image/jpeg",
      });
      setImages(prev => [...prev, dataUrl]);
    } catch {
      toast.error("Couldn't read that image");
    } finally {
      setImageUploading(false);
    }
  };

  const removeImageAt = (idx) => {
    setImages(prev => prev.filter((_, i) => i !== idx));
  };

  const toggleArea = (key) => {
    setWheelAreaKeys(prev => {
      if (prev.includes(key)) return prev.filter(k => k !== key);
      if (prev.length >= MAX_AREAS) { toast.error(`Up to ${MAX_AREAS} life areas per card`); return prev; }
      return [...prev, key];
    });
  };

  const commitTag = () => {
    const raw = tagDraft.replace(/^#+/, "").trim();
    if (!raw) return;
    if (tags.length >= MAX_TAGS) { toast.error(`Up to ${MAX_TAGS} tags per card`); return; }
    if (raw.length > 30) { toast.error("Tags max 30 characters"); return; }
    if (tags.some(t => t.toLowerCase() === raw.toLowerCase())) { setTagDraft(""); return; }
    setTags(prev => [...prev, raw]);
    setTagDraft("");
  };

  const save = async () => {
    if (!content.trim() && images.length === 0) {
      toast.error("Add a note or an image");
      return;
    }
    // Promote any in-flight tag draft so a user who typed "food" and clicked
    // Save without hitting Enter doesn't lose their last tag.
    const draft = tagDraft.replace(/^#+/, "").trim();
    let finalTags = tags;
    if (draft && !tags.some(t => t.toLowerCase() === draft.toLowerCase()) && tags.length < MAX_TAGS) {
      finalTags = [...tags, draft];
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/gratitude/cards/${card.card_id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          content: content.trim(),
          image_data_urls: images,
          wheel_area_keys: wheelAreaKeys,
          tags: finalTags,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Save failed");
      }
      const updated = await res.json();
      toast.success("Saved");
      onSaved?.(updated);
      onClose();
    } catch (e) {
      toast.error(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const charCount = content.length;
  const overLimit = charCount > MAX_CONTENT;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-lg p-0 overflow-hidden flex flex-col"
        style={{ maxHeight: "90vh" }}
        data-testid="edit-gratitude-dialog"
      >
        <div className="px-6 pt-5 pb-2 flex-shrink-0">
          <DialogTitle
            className="text-lg font-semibold"
            style={{ color: "var(--color-text-primary)" }}
          >
            Edit gratitude
          </DialogTitle>
        </div>

        <div className="px-6 pb-5 space-y-4 flex-1 overflow-y-auto min-h-0">
          <div>
            <Label
              className="text-xs mb-2 block font-semibold"
              style={{ color: "var(--color-text-label)", textTransform: "uppercase", letterSpacing: "0.08em" }}
            >
              Your note
            </Label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="I am grateful…"
              rows={8}
              maxLength={MAX_CONTENT}
              className="resize-y min-h-[11rem] text-[15px] leading-relaxed"
              data-testid="edit-gratitude-content"
              autoFocus
            />
            <p
              className="text-xs mt-1 text-right"
              style={{ color: overLimit ? "#c0392b" : "var(--color-text-secondary)" }}
              data-testid="edit-gratitude-char-count"
            >
              {charCount} / {MAX_CONTENT}
            </p>
          </div>

          <div>
            <Label
              className="text-xs mb-2 block font-semibold"
              style={{ color: "var(--color-text-label)", textTransform: "uppercase", letterSpacing: "0.08em" }}
            >
              Images <span className="font-normal normal-case opacity-60">({images.length}/{MAX_IMAGES})</span>
            </Label>
            {images.length > 0 && (
              <div className="grid grid-cols-2 gap-2 mb-2" data-testid="edit-image-grid">
                {images.map((src, idx) => (
                  <div
                    key={idx}
                    className="relative rounded-lg overflow-hidden"
                    style={{ border: "1px solid var(--color-border-default)", aspectRatio: "4 / 3" }}
                  >
                    <img
                      src={src}
                      alt={`Attached ${idx + 1}`}
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                    <button
                      onClick={() => removeImageAt(idx)}
                      className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center hover:bg-black/80 transition-colors"
                      style={{ background: "rgba(0,0,0,0.6)", color: "#fff" }}
                      aria-label="Remove image"
                      data-testid={`edit-image-remove-${idx}`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {images.length < MAX_IMAGES && (
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={imageUploading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm transition-colors"
                style={{
                  background: "var(--color-surface-subtle)",
                  color: "var(--color-text-secondary)",
                  border: "1px dashed var(--color-border-default)",
                }}
                data-testid="edit-image-pick"
              >
                <ImagePlus size={14} />
                {imageUploading
                  ? "Processing…"
                  : (images.length === 0 ? "Add a photo" : "Add another photo")}
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleImagePick}
            />
          </div>

          {/* Life areas — multi-select chips (mirrors the add-dialog picker) */}
          {wheelAreas.length > 0 && (
            <div>
              <Label
                className="text-xs mb-2 block font-semibold"
                style={{ color: "var(--color-text-label)", textTransform: "uppercase", letterSpacing: "0.08em" }}
              >
                Life areas <span className="font-normal normal-case opacity-60">(pick any)</span>
              </Label>
              <div className="flex gap-1.5 flex-wrap" data-testid="edit-area-picker">
                <button
                  onClick={() => setWheelAreaKeys([])}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all"
                  style={{
                    background: wheelAreaKeys.length === 0 ? "var(--color-brand-primary)" : "transparent",
                    color: wheelAreaKeys.length === 0 ? "var(--color-text-on-brand)" : "var(--color-text-secondary)",
                    border: `1px solid ${wheelAreaKeys.length === 0 ? "var(--color-brand-primary)" : "var(--color-border-default)"}`,
                  }}
                  data-testid="edit-area-none"
                >
                  {wheelAreaKeys.length === 0 && <Check size={10} />}
                  No area
                </button>
                {wheelAreas.map(a => {
                  const active = wheelAreaKeys.includes(a.key);
                  return (
                    <button
                      key={a.key}
                      onClick={() => toggleArea(a.key)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all"
                      style={{
                        background: active ? a.color : "transparent",
                        color: active ? "#fff" : "var(--color-text-primary)",
                        border: `1px solid ${active ? a.color : "var(--color-border-default)"}`,
                      }}
                      data-testid={`edit-area-${a.key}`}
                      title={a.label}
                    >
                      {active && <Check size={10} />}
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{
                          background: active ? "#fff" : a.color,
                          opacity: active ? 0.9 : 1,
                        }}
                      />
                      {a.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tags — free-form hashtag-style labels */}
          <div>
            <Label
              className="text-xs mb-2 block font-semibold"
              style={{ color: "var(--color-text-label)", textTransform: "uppercase", letterSpacing: "0.08em" }}
            >
              Tags <span className="font-normal normal-case opacity-60">(optional)</span>
            </Label>
            <div
              className="flex flex-wrap gap-1.5 items-center rounded-lg px-2 py-2"
              style={{
                border: "1px solid var(--color-border-default)",
                background: "var(--color-surface)",
              }}
              onClick={(e) => {
                const inp = e.currentTarget.querySelector('input[data-testid="edit-tag-input"]');
                if (inp) inp.focus();
              }}
              data-testid="edit-tag-editor"
            >
              {tags.map((t, i) => (
                <span
                  key={`${t}-${i}`}
                  className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs font-medium"
                  style={{
                    background: "var(--color-brand-ghost)",
                    color: "var(--color-brand-primary)",
                  }}
                  data-testid={`edit-tag-chip-${t.toLowerCase()}`}
                >
                  #{t}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setTags(prev => prev.filter((_, idx) => idx !== i));
                    }}
                    className="w-4 h-4 inline-flex items-center justify-center rounded-full hover:bg-black/10"
                    aria-label={`Remove tag ${t}`}
                    data-testid={`edit-tag-remove-${t.toLowerCase()}`}
                  >
                    <X size={10} />
                  </button>
                </span>
              ))}
              <input
                value={tagDraft}
                onChange={(e) => setTagDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "," || e.key === " ") {
                    e.preventDefault();
                    commitTag();
                  } else if (e.key === "Backspace" && !tagDraft && tags.length) {
                    setTags(prev => prev.slice(0, -1));
                  }
                }}
                placeholder={tags.length ? "Add another…" : "e.g. food, uruguay, life"}
                className="flex-1 min-w-[8rem] bg-transparent outline-none text-sm"
                style={{ color: "var(--color-text-primary)" }}
                data-testid="edit-tag-input"
              />
            </div>
            <p className="text-[10px] mt-1" style={{ color: "var(--color-text-placeholder)" }}>
              Press Enter, comma, or space to add. Up to {MAX_TAGS} tags.
            </p>
          </div>
        </div>

        <div
          className="flex items-center justify-between gap-3 px-6 py-3 flex-shrink-0"
          style={{ borderTop: "1px solid var(--color-border-default)", background: "var(--color-surface-subtle)" }}
        >
          <Button
            variant="ghost"
            onClick={onClose}
            className="rounded-full h-9 text-sm"
            data-testid="edit-gratitude-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving || imageUploading || overLimit || (!content.trim() && images.length === 0)}
            className="rounded-full h-9 px-5"
            style={{ background: "var(--color-brand-primary)", color: "var(--color-text-on-brand)" }}
            data-testid="edit-gratitude-save"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditGratitudeDialog;
